import { Component, OnInit, inject } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom, take, tap } from 'rxjs';
import { Order } from './shared/classes/order';
import { Supplier } from './shared/classes/supplier';
import { StartupState } from './shared/classes/startup-state.enum';
import { AppShellComponent } from './app-shell/app-shell.component';
import { StartupLoadingDialogComponent } from './popups/startup-loading-dialog/startup-loading-dialog.component';
import { AuthService } from './shared/services/auth.service';
import { SupplierApiService } from './shared/services/supplier-api.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    imports: [AppShellComponent]
})
export class AppComponent implements OnInit {
    private dialog = inject(MatDialog);
    private authService = inject(AuthService);
    private supplierApiService = inject(SupplierApiService);

    public readonly startupStates: typeof StartupState = StartupState;

    public shellVisible = false;
    public startupState: StartupState = StartupState.LOADING;
    public loadingError: string;
    public userOrders: Order[] = [];
    public supplier: Supplier = new Supplier();

    private startupDialogRef: MatDialogRef<StartupLoadingDialogComponent>;

    async ngOnInit(): Promise<void> {
        this.loadingError = undefined;
        this.startupState = StartupState.LOADING;

        try {
            const isAuthenticated = await firstValueFrom(this.authService.initAuth());
            if (!isAuthenticated) {
                this.startupState = StartupState.REDIRECTING;
                return;
            }

            await this.initializeApp();
        } catch (error) {
            this.loadingError = error instanceof Error ? error.message : `${error}`;
            this.startupState = StartupState.ERROR;
        }
    }

    private async initializeApp(): Promise<void> {
        this.openStartupDialog();

        try {
            const { orders, supplier } = await this.supplierApiService.fetchStartupData();

            this.userOrders = orders;
            this.supplier = supplier;
            this.startupState = StartupState.READY;
            await this.closeStartupDialog();
            this.shellVisible = true;
        } catch (error) {
            this.loadingError = error instanceof Error ? error.message : `${error}`;
            this.startupState = StartupState.ERROR;
            this.startupDialogRef?.componentInstance.showError(this.loadingError);
        }
    }

    private openStartupDialog(): void {
        if (this.startupDialogRef) {
            return;
        }

        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = true;

        const dialogRef = this.dialog.open(StartupLoadingDialogComponent, dialogConfig);
        dialogRef
            .afterClosed()
            .pipe(
                take(1),
                tap(() => {
                    if (this.startupDialogRef === dialogRef) {
                        this.startupDialogRef = undefined;
                    }
                })
            )
            .subscribe();
        this.startupDialogRef = dialogRef;
    }

    private async closeStartupDialog(): Promise<void> {
        if (!this.startupDialogRef) {
            return;
        }

        const dialogRef = this.startupDialogRef;
        dialogRef.close();
        await firstValueFrom(dialogRef.afterClosed());
    }
}
